import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
} from 'react';
import PropTypes from 'prop-types';
import {
  Form,
  Input,
  Button,
  Spin,
  Row,
  Col,
  Modal,
  message,
  Grid,
  Typography,
  Tooltip,
} from 'antd';
import {
  SearchOutlined,
  DeleteOutlined,
  ArrowLeftOutlined,
  ShoppingCartOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import Moment from 'moment';
import FormSubmitControls from 'components/FormSubmitCustomControls';
import Table from 'components/Table';
import {
  RFC_PATTERN,
  CURP_PATTERN,
  digitoVerificador,
  INE_PATTERN,
} from 'utils/patterns';
import LadaNumero from 'components/LadaNumero';
import { useSelector } from 'react-redux';
import { getEstadosCiviles, getGeneros } from 'api/usuarios';
import { postCiudadano, baseURI as ciudadanoURI, getCiudadanos } from 'api/cuentaunicasir';
import { hasActionPermission, ActionsNames, getModelPermission } from 'utils/permission/index';
// import { capitalizeFirstLetter } from 'utils/URIFormat';
import { onError } from 'utils/handlers';
import { getCiudadanoCaja } from 'api/cuentaunicasir/index';
import { DATE_FORMAT, DATE_FRONTEND_FORMAT } from 'utils/formatValues';
import NumericInput from 'components/NumericInput';
import { nameValidationSearch } from 'utils/nameValidationSearch';
import ModalDireccion from 'components/ModalDireccion';
import Select from 'components/Select';
import { capitalize } from 'utils/normalizers';
import { generateCURPRules } from 'utils/rules';
import { selectPublicoGeneral } from 'store/reducers/auth';

const { useBreakpoint } = Grid;

const models = ['ciudadano', 'estadocivil', 'genero'];

const BusquedaAvanzada = ({
  visible,
  setVisible,
  callback,
  dataExterno,
  setDataExterno,
  disabled,
  setCiudadanoQ,
  ciudadanoQ,
  msgAsignarEntidad,
  visibleButton,
}) => {
  const pg = useSelector(selectPublicoGeneral);
  const addressPermission = useMemo(() => getModelPermission('direccion'), []);
  const screen = useBreakpoint();
  const [form] = Form.useForm();
  const [formAddress] = Form.useForm();
  const [formCiudadano] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ count: 1, results: [] });
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const { generos, estadosCiviles } = useSelector((state) => state.catalogs);
  const [visibleAdd, setVisibleAdd] = useState(false);
  const [hasAllViewPermission, setHasAllViewPermission] = useState(true);
  const [canAddCiudadano, setCanAddCiudadano] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [parentID, setParentID] = useState(0);
  const btnAddressRef = useRef();

  useEffect(() => {
    let mounted = true;
    let _hasAllViewPermission = true;
    const validatePermission = () => {
      _hasAllViewPermission = models
        .map((m) => hasActionPermission(m, ActionsNames.view))
        .every((e) => e);
      if (mounted) {
        setHasAllViewPermission(_hasAllViewPermission);
        setCanAddCiudadano(hasActionPermission('ciudadano', ActionsNames.add));
      }
    };
    const fetchData = async () => {
      setLoading(true);
      if (!generos.length) await getGeneros();
      if (!estadosCiviles.length) await getEstadosCiviles();
      setLoading(false);
    };
    if (mounted) {
      validatePermission();
      fetchData();
    }
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (dataExterno?.results?.length) setData(dataExterno);
    // eslint-disable-next-line
  }, [dataExterno]);

  const onCancel = () => {
    setVisible(false);
    setData({ count: 1, results: [] });
    if (setDataExterno) setDataExterno({ count: 1, results: [] });
    setSelectedRowKeys([]);
    form.resetFields();
    setCurrentPage(1);
  };

  const onCancelAdd = (ciudadanoNuevo) => {
    setVisibleAdd(false);
    formCiudadano.resetFields();
    if (typeof setCiudadanoQ === 'function') {
      setCiudadanoQ();
    }
    if (ciudadanoNuevo) {
      callback({
        ...ciudadanoNuevo,
        nombre_completo: `${ciudadanoNuevo.nombre
          || ''} ${ciudadanoNuevo.apellido_paterno
          || ''} ${ciudadanoNuevo.apellido_materno
          || ''}`,
      });
    }
  };

  const handleSearch = async (page = 1, q = null, ignore = false) => {
    try {
      setLoading(true);
      setCurrentPage(page);
      await form.validateFields();
      setSelectedRowKeys([]);
      const values = form.getFieldsValue();
      const keys = Object.keys(values);
      const validProps = keys.filter((prop) => values[prop]);
      const validationName = nameValidationSearch(validProps);

      if (!(validProps.length || ignore)) {
        message.warn('Debe ingresar al menos un campo');
        setLoading(false);
        return;
      }
      if (!validationName) {
        message.warn('Debe ingresar mínimo dos campos referentes a nombre');
        setLoading(false);
        return;
      }
      const results = await getCiudadanoCaja({
        page,
        ...values,
        ...{ ...q, ...ciudadanoQ },
      });

      const _data = results?.results || results;
      const filtered = _data.filter((item) => item.id !== pg);

      if (filtered.length) {
        setData(filtered);
      } else {
        message.warn(`No se encontró ningún ciudadano relacionado. 
    ${_data.some((item) => item.id === pg)
    ? 'Recuerde que el publico general no está disponible en las búsquedas.' : ''}`);
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const onFinishSearch = async (record) => {
    if (typeof setCiudadanoQ === 'function') {
      setCiudadanoQ();
    }
    if (!record.fromThisEntity) {
      msgAsignarEntidad(record);
    } else {
      callback(record);
    }
    onCancel();
  };

  const onFinish = async () => {
    setLoading(true);
    await formCiudadano.validateFields();

    const values = formCiudadano.getFieldsValue();
    if (!values.email) {
      delete values.email;
    }
    const saved = await postCiudadano(values);

    if (saved) {
      if (values.agregarDireccion) {
        setParentID(saved.id);
        btnAddressRef.current.click();
      }
      onCancelAdd(saved);
      setVisible(false);
      formCiudadano.resetFields();
    }
    setLoading(false);
  };

  const columns = [
    {
      titleText: 'Clave',
      dataIndex: 'clave_ciudadana',
      key: 'clave_ciudadana',
      render: (text, record) => (
        <Button
          type="link"
          className="primary-text no-color"
          onClick={() => onFinishSearch(record)}
        >
          {text}
        </Button>
      ),
      width: 250,
    },
    {
      titleText: 'Correo',
      dataIndex: 'email',
      key: 'email',
      width: 300,
    },
    {
      titleText: 'Nombre Completo',
      dataIndex: 'nombre_completo',
      key: 'nombre_completo',
      width: 200,
    },
    {
      titleText: 'Celular',
      dataIndex: 'numero_de_celular',
      key: 'numero_de_celular',
      width: 200,
    },
    {
      titleText: 'CURP',
      dataIndex: 'CURP',
      key: 'CURP',
      width: 200,
    },
    {
      titleText: 'RFC',
      dataIndex: 'RFC',
      key: 'RFC',
      width: 200,
    },
    {
      titleText: 'Correo',
      dataIndex: 'email',
      key: 'email',
      width: 300,
    },
    {
      titleText: 'Fecha de Nacimiento',
      dataIndex: 'fecha_nacimiento',
      key: 'fecha_nacimiento',
      width: 300,
      render: (val) => (val ? new Moment(val, DATE_FORMAT)?.format(DATE_FRONTEND_FORMAT) : ''),
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const add = () => {
    const [key] = selectedRowKeys;
    const match = (data.results || data).find((item) => item.id === key);
    onFinishSearch(match);
  };

  const searchRules = {
    email: [{ type: 'email' }],
    RFC: [
      {
        pattern: RFC_PATTERN,
        message: 'Ingrese un RFC válido',
      },
    ],
    CURP: [
      {
        validator: async (rule, value) => {
          if (value) {
            const validado = value.toUpperCase().match(CURP_PATTERN);
            if (!validado || validado[2] !== digitoVerificador(validado[1])) {
              throw new Error('Ingrese una CURP válida');
            }
          }
        },
      },
    ],
    INE: [
      {
        validator: async (rule, value) => {
          if (value) {
            if (!INE_PATTERN.test(value)) {
              throw new Error('Ingrese un INE válido');
            }
          }
        },
      },
    ],
    celular: [
      { required: false },
      {
        pattern: /^(?:[0-9]{10}|)$/,
        message: 'Ingrese un número telefónico válido',
      },
    ],
  };

  const rules = {
    required: [{ required: true }],
    email: [{ type: 'email' }],
    RFC: [
      {
        validator: async (_, RFC) => {
          // if (!RFC && !visibleAdd) {
          //   throw new Error('Este campo es requerido');
          // }
          if (visibleAdd && RFC) {
            const _padron = await getCiudadanos({ RFC });
            const match = _padron?.results.find((e) => e?.RFC === RFC);
            if (_padron.count
              && _padron?.results?.length
              && match) {
              throw new Error('Ya existe un contribuyente con este RFC.');
            } else if (!RFC_PATTERN.test(RFC)) {
              throw new Error('Ingrese un RFC válido.');
            }
          }
        },
      },
    ],
    CURP: generateCURPRules({ validateUnique: true, required: false, tipoDePadron: 1 }),
    INE: [
      {
        validator: async (rule, value) => {
          if (value) {
            if (!INE_PATTERN.test(value)) {
              throw new Error('Ingrese un INE válido');
            }
          }
        },
      },
    ],
    celular: [
      { required: false },
      {
        pattern: /^(?:[0-9]{10}|)$/,
        message: 'Ingrese un número telefónico válido',
      },
    ],
  };

  const contolsAdd = [
    {
      text: 'Regresar',
      icon: <ArrowLeftOutlined />,
      onClick: () => onCancelAdd(),
    },
    {
      text: 'Agregar',
      icon: <PlusOutlined />,
      onClick: () => onFinish(),
    },
  ];

  const controls = [
    {
      text: 'Regresar',
      icon: <ArrowLeftOutlined />,
      onClick: () => onCancel(),
    },
    hasAllViewPermission && {
      text: 'Limpiar',
      icon: <DeleteOutlined />,
      onClick: () => {
        setData([]);
        form.resetFields();
      },
    },
  ];

  const getOnFinish = () => {
    handleSearch();
  };

  const FormAddCiudadano = () => (
    <Form
      layout="vertical"
      form={formCiudadano}
      scrollToFirstError
      onFinish={() => onFinish()}
      initialValues={{
        agregarDireccion: true,
      }}
    >
      <Row gutter={10}>
        <Col xs={24} sm={24} md={8}>
          <Form.Item
            name="nombre"
            rules={rules.required}
            label="Nombre"
            hasFeedback
            normalize={capitalize}
          >
            <Input allowClear />
          </Form.Item>
        </Col>

        <Col xs={24} sm={24} md={8}>
          <Form.Item
            name="apellido_paterno"
            rules={rules.required}
            label="Apellido paterno"
            hasFeedback
            normalize={capitalize}
          >
            <Input allowClear />
          </Form.Item>
        </Col>

        <Col xs={24} sm={24} md={8}>
          <Form.Item
            name="apellido_materno"
            label="Apellido materno"
            hasFeedback
            normalize={capitalize}
          >
            <Input />
          </Form.Item>
        </Col>
        <Col md={8} sm={24} xs={24}>
          <Form.Item
            label="CURP"
            rules={rules.CURP}
            name="CURP"
            normalize={(value) => value.toUpperCase()}
            validateTrigger="onBlur"
          >
            <Input allowClear />
          </Form.Item>
        </Col>
        <Col md={8} sm={24} xs={24}>
          <Form.Item
            label="RFC"
            rules={rules.RFC}
            name="RFC"
            normalize={(val) => val?.toUpperCase()}
            validateTrigger="onBlur"
          >
            <Input allowClear />
          </Form.Item>
        </Col>
        <Col xs={24} sm={24} md={8}>
          <Form.Item
            name="email"
            rules={rules.email}
            label="Correo electrónico"
            hasFeedback
            normalize={capitalize}
          >
            <Input allowClear />
          </Form.Item>
        </Col>
        <Col md={16} sm={24} xs={24}>
          <LadaNumero
            form={form}
            rules={rules}
            required
            fullSize
            names={{
              lada: 'lada',
              numero_de_telefono: 'numero_de_celular',
            }}
            celular
          />
        </Col>
        <Col xs={24} sm={24} md={8}>
          <Form.Item
            name="agregarDireccion"
            label="Agregar dirección"
          >
            <Select trueFalse />
          </Form.Item>
        </Col>
      </Row>
      <Form.Item hidden>
        <Button htmlType="submit" />
      </Form.Item>
    </Form>
  );

  return (
    <>
      {visibleButton && (
        <Row>
          <Col span={24}>
            <Button
              onClick={() => setVisible(true)}
              className="primary-on-hover"
              disabled={disabled}
              style={{
                height: !screen.xs && 64,
                width: !screen.xs && 64,
                marginTop: -5,
              }}
            >
              ...
            </Button>
          </Col>
        </Row>
      )}
      <Modal
        width="90%"
        visible={visible}
        closable={false}
        footer={null}
        title={(
          <FormSubmitControls
            label={visibleAdd ? 'Agregar Ciudadano' : 'Búsqueda Avanzada'}
            controls={visibleAdd ? contolsAdd : controls}
            loading={loading}
          />
        )}
        onCancel={onCancel}
        forceRender
        maskClosable={false}
        destroyOnClose
        centered
      >
        {hasAllViewPermission ? (
          <Spin tip="Cargando..." spinning={loading}>
            {visibleAdd ? (
              <FormAddCiudadano />
            ) : (
              <Form
                form={form}
                layout="vertical"
                onFinish={() => getOnFinish()}
              >
                <Row gutter={10}>
                  <Col md={6} sm={24} xs={24}>
                    <Form.Item
                      label="Clave ciudadana"
                      name="clave_ciudadana"
                      normalize={capitalize}
                    >
                      <Input allowClear />
                    </Form.Item>
                  </Col>
                  <Col md={6} sm={24} xs={24}>
                    <Form.Item
                      label="Nombre"
                      name="nombre"
                      normalize={capitalize}
                    >
                      <Input allowClear />
                    </Form.Item>
                  </Col>
                  <Col md={6} sm={24} xs={24}>
                    <Form.Item
                      label="Apellido paterno"
                      name="apellido_paterno"
                      normalize={capitalize}
                    >
                      <Input allowClear />
                    </Form.Item>
                  </Col>
                  <Col md={6} sm={24} xs={24}>
                    <Form.Item
                      label="Apellido materno"
                      name="apellido_materno"
                      normalize={capitalize}
                    >
                      <Input allowClear />
                    </Form.Item>
                  </Col>

                  <Col md={6} sm={24} xs={24}>
                    <Form.Item
                      label="Correo"
                      rules={searchRules.email}
                      name="email"
                      normalize={capitalize}
                    >
                      <Input allowClear />
                    </Form.Item>
                  </Col>
                  <Col md={6} sm={24} xs={24}>
                    <NumericInput
                      label="No. celular"
                      name="numero_de_celular"
                    />
                  </Col>
                  <Col md={6} sm={24} xs={24}>
                    <Form.Item
                      label="CURP"
                      rules={searchRules.CURP}
                      name="CURP"
                      normalize={(value) => value.toUpperCase()}
                    >
                      <Input allowClear />
                    </Form.Item>
                  </Col>
                  <Col md={6} sm={24} xs={24}>
                    <Form.Item
                      label="RFC"
                      rules={searchRules.RFC}
                      name="RFC"
                      normalize={(val) => val?.toUpperCase()}
                    >
                      <Input allowClear />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={[10, 5]} justify="end">
                  {canAddCiudadano ? (
                    <Col xs={24} sm={24} md={8} lg={8} xl={6}>
                      <Button
                        block
                        onClick={() => {
                          const values = form.getFieldsValue();
                          formCiudadano.setFieldsValue(values);
                          setVisibleAdd(true);
                        }}
                        icon={<PlusOutlined />}
                      >
                        Agregar Ciudadano
                      </Button>
                    </Col>
                  ) : (
                    <Tooltip title="No cuenta con los permisos para agregar ciudadanos">
                      <Col xs={24} sm={24} md={8} lg={8} xl={6}>
                        <Button
                          block
                          disabled
                          icon={<PlusOutlined />}
                        >
                          Agregar Ciudadano
                        </Button>
                      </Col>
                    </Tooltip>
                  )}
                  <Col xs={24} sm={24} md={6} lg={4}>
                    <Button
                      block
                      htmlType="submit"
                      icon={(<SearchOutlined />)}
                      disabled={loading}
                    >
                      Buscar
                    </Button>
                  </Col>
                  <Col xs={24} sm={24} md={6} lg={4}>
                    <Button
                      onClick={() => add()}
                      block
                      disabled={!selectedRowKeys.length || loading}
                      icon={<ShoppingCartOutlined />}
                    >
                      Cargar
                    </Button>
                  </Col>
                </Row>
                <br />
              </Form>
            )}
            {!visibleAdd && (
              <Table
                cols={columns}
                data={'count' in data ? data.results : data}
                noControls
                allowSort={false}
                allowSearch={false}
                rowSelection={rowSelection}
                handleOnRowClick={(record) => {
                  setSelectedRowKeys([record.id]);
                }}
                rowKey="id"
                hasAllViewPermission={hasAllViewPermission}
                currentPage={currentPage}
                count={data.count}
                paginationCallback={handleSearch}
                pagination={false}
                mobileColIndex={0}
              />
            )}
          </Spin>
        ) : (
          <Typography.Text style={{ display: 'grid', placeContent: 'center' }}>
            No cuenta con los permisos necesarios para consultar ciudadanos
          </Typography.Text>
        )}
      </Modal>
      <div style={{ display: 'none' }}>
        <ModalDireccion
          form={formAddress}
          onClick={() => setVisible(false)}
          parentID={parentID}
          currentURI={ciudadanoURI}
          permission={addressPermission}
          _ref={btnAddressRef}
        />
      </div>
    </>
  );
};

BusquedaAvanzada.propTypes = {
  callback: PropTypes.func.isRequired,
  visible: PropTypes.bool.isRequired,
  setVisible: PropTypes.func.isRequired,
  dataExterno: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number,
      clave_ciudadana: PropTypes.string,
      foto: PropTypes.string,
      CURP: PropTypes.string,
      licencia_de_conducir: PropTypes.string,
      INE: PropTypes.string,
      RFC: PropTypes.string,
      fecha_nacimiento: PropTypes.string,
      email: PropTypes.string,
      nombre: PropTypes.string,
      apellido_paterno: PropTypes.string,
      apellido_materno: PropTypes.string,
      tema: PropTypes.string,
      email_alternativo: PropTypes.string,
      numero_de_celular: PropTypes.number,
      lada: PropTypes.number,
      numero_verificado: PropTypes.bool,
      direccion: PropTypes.string,
      estado_civil: PropTypes.number,
      genero: PropTypes.number,
      estados_globales: PropTypes.number,
    })),
    PropTypes.shape({
      results: PropTypes.arrayOf(PropTypes.shape({})),
    }),
  ]),
  setDataExterno: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  setCiudadanoQ: PropTypes.func,
  ciudadanoQ: PropTypes.shape(),
  msgAsignarEntidad: PropTypes.func.isRequired,
  visibleButton: PropTypes.bool,
};

BusquedaAvanzada.defaultProps = {
  dataExterno: [],
  disabled: false,
  setCiudadanoQ: null,
  ciudadanoQ: null,
  visibleButton: true,
};

export default BusquedaAvanzada;
